import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API } from './config';
import { SETTINGS_KEYS } from './perms';

const KEY = 'tim456_auth';

function load() {
  try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch { return null; }
}

function applyToken(token) {
  if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  else delete axios.defaults.headers.common['Authorization'];
}

// All'avvio del modulo: ripristina il token salvato sull'header di axios.
const _initial = load();
applyToken(_initial?.token);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(_initial);

  // `verifica` controlla le credenziali SENZA entrare: serve alla pagina d'accesso, che
  // dopo il via libera lascia finire l'animazione d'uscita e solo allora apre l'app.
  // Senza questa separazione l'applicazione comparirebbe a meta' animazione.
  const verifica = useCallback(async (username, password) => {
    const r = await axios.post(`${API}/auth/login`, { username, password });
    return { token: r.data.token, username: r.data.username, ruolo: r.data.ruolo, permessi: r.data.permessi || [] };
  }, []);

  const applica = useCallback((data) => {
    localStorage.setItem(KEY, JSON.stringify(data));
    applyToken(data.token);
    setAuth(data);
    return data;
  }, []);

  const login = useCallback(async (username, password) => {
    return applica(await verifica(username, password));
  }, [verifica, applica]);

  const logout = useCallback(() => {
    localStorage.removeItem(KEY);
    applyToken(null);
    setAuth(null);
  }, []);

  const can = useCallback((key) => {
    if (!auth) return false;
    if (auth.ruolo === 'admin') return true;
    const perms = auth.permessi || [];
    // "impostazioni" = accesso alla pagina se c'è almeno una sotto-sezione abilitata.
    if (key === 'impostazioni') return SETTINGS_KEYS.some((k) => perms.includes(k));
    return perms.includes(key);
  }, [auth]);

  // Interceptor: a ogni 401 (token scaduto/invalido) torna alla schermata di login.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem(KEY);
          applyToken(null);
          setAuth(null);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  // All'avvio: aggiorna ruolo/permessi dal server (così i cambi dell'admin si applicano).
  useEffect(() => {
    if (!_initial?.token) return;
    axios.get(`${API}/auth/me`).then((r) => {
      setAuth((prev) => {
        if (!prev) return prev;
        const data = { ...prev, ruolo: r.data.ruolo, permessi: r.data.permessi || [] };
        localStorage.setItem(KEY, JSON.stringify(data));
        return data;
      });
    }).catch(() => {});
  }, []);

  return (
    <AuthContext.Provider value={{ user: auth, login, verifica, applica, logout, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// URL per media serviti da /api (audio): allega il token come query param,
// perché gli elementi <audio>/<img> nativi non inviano l'header Authorization.
export function mediaUrl(path) {
  const t = load()?.token;
  if (!t) return `${API}${path}`;
  const sep = path.includes('?') ? '&' : '?';
  return `${API}${path}${sep}token=${encodeURIComponent(t)}`;
}
